import { useEffect, useState } from "react";

import AssistantPanel from "./AssistantPanel";
import "./AssistantDock.css";

/** The assistant on every screen that is not the dashboard.
 *
 *  1v1 asked for this: they wanted it to travel with them rather than being
 *  somewhere they navigate to. Their case was the obvious one, that having to
 *  leave the project you are reading in order to ask about it is the reason
 *  nobody asks.
 *
 *  A dock rather than a permanent rail, because every screen except the
 *  dashboard is drawn full width in the design and a 400px column taken out of
 *  the match list would cost more than it gives. Closed it is a button; open
 *  it is the same panel the dashboard shows, so there is one assistant rather
 *  than two that drift apart.
 *
 *  Whether it is open is remembered for the session. Somebody who opens it on
 *  one screen is working with it, and closing itself on every navigation would
 *  be the same annoyance in a different form. */
const REMEMBERED = "castflow.assistant.open";

function wasOpen(): boolean {
  try {
    return sessionStorage.getItem(REMEMBERED) === "1";
  } catch {
    // Private windows and blocked storage both throw here, and neither is a
    // reason to fail to draw the button.
    return false;
  }
}

export default function AssistantDock() {
  const [open, setOpen] = useState(wasOpen);

  useEffect(() => {
    try {
      sessionStorage.setItem(REMEMBERED, open ? "1" : "0");
    } catch {
      // Nothing to do. It reopens closed next time, which is survivable.
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        className={`dock__launch ${open ? "dock__launch--hidden" : ""}`}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-label="Open the assistant"
        aria-hidden={open}
        tabIndex={open ? -1 : 0}
      >
        <span className="dock__spark" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 1.6l1.5 3.9 3.9 1.5-3.9 1.5L8 12.4 6.5 8.5 2.6 7l3.9-1.5L8 1.6Z"
              fill="currentColor"
            />
          </svg>
        </span>
        <span className="dock__label">Ask</span>
      </button>

      <aside className={`dock ${open ? "dock--open" : ""}`} aria-label="AI assistant" aria-hidden={!open}>
        <button
          className="dock__close"
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close the assistant"
          tabIndex={open ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <AssistantPanel active={open} />
      </aside>
    </>
  );
}
